import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

interface OdometerProps {
  /** Display value, e.g. "11+", "48", "98.6%", "1,200". Non-digit chars render statically. */
  value: string;
  className?: string;
  /** Seconds the digits take to settle. */
  durationSec?: number;
}

/**
 * Truck-odometer style number: each digit spins up to its target on the first
 * time it scrolls into view. Non-digit characters (".", ",", "%", "+", prefixes)
 * render as static glyphs so values like "98.6%" or "1,200+" work unchanged.
 *
 * Honors prefers-reduced-motion by rendering the final value immediately.
 */
export function Odometer({ value, className, durationSec = 1.4 }: OdometerProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const reduceMotion = useReducedMotion();
  const animate = inView && !reduceMotion;

  const chars = value.split("");
  // Stagger from the most-significant digit so it reads like a mechanical roll.
  const digitCount = chars.filter((c) => /\d/.test(c)).length;
  let digitIndex = -1;

  return (
    <span ref={ref} className={cn("inline-flex items-end tabular-nums leading-none", className)}>
      {chars.map((char, i) => {
        if (!/\d/.test(char)) {
          return (
            <span key={i} className="inline-block">
              {char}
            </span>
          );
        }
        digitIndex += 1;
        const target = Number(char);
        const delay = (digitIndex / Math.max(digitCount, 1)) * 0.35;
        return (
          <span
            key={i}
            className="relative inline-block overflow-hidden"
            style={{ height: "1em", width: "0.62em" }}
            aria-hidden="true"
          >
            <motion.span
              className="absolute left-0 top-0 flex w-full flex-col items-center"
              initial={{ y: reduceMotion ? `-${target * 10}%` : "0%" }}
              animate={{ y: animate ? `-${target * 10}%` : reduceMotion ? `-${target * 10}%` : "0%" }}
              transition={{ duration: durationSec, delay, ease: [0.16, 1, 0.3, 1] }}
            >
              {Array.from({ length: 10 }, (_, n) => (
                <span key={n} style={{ height: "1em", lineHeight: "1em" }}>
                  {n}
                </span>
              ))}
            </motion.span>
          </span>
        );
      })}
      {/* Accessible, non-animated value for screen readers. */}
      <span className="sr-only">{value}</span>
    </span>
  );
}
